import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';

import { Dialog } from 'components/common';
import { Input } from 'components/form';
import { required } from 'components/form/validationRules';
import NumberInputFieldWithActions from 'components/inputs/NumberInputFieldWithActions';
import ImageInputField from './ImageInputField';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: theme.spacing(100),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  root: {
    maxWidth: '840px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  fieldsColumn: {
    flex: 1,
    minWidth: 200,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputNoMargin: {
    marginTop: 0,
  },
  sectionTitle: {
    marginTop: theme.spacing(4),
    fontWeight: 600,
    color: theme.palette.secondary.contrastText,
  },
  numberFields: {
    marginTop: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  adornmentIcon: {
    color: theme.palette.primary.main,
  },
  description: {
    marginTop: theme.spacing(1),
    color: theme.palette.secondary.contrastText,
  },
}));

interface EditLevelDialogProps {
  open: boolean;
  onClose: () => void;
  level?: any;
}

interface FormValues {
  icon?: File | string | null;
  name: string;
  minPoints: string;
  minShipments: string;
  minSpend: string;
}

const parseNumber = (value: string | number | undefined | null) => {
  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    return value;
  }

  return '0';
};

const EditLevelDialog = ({ open, onClose, level }: EditLevelDialogProps) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const initialValues: FormValues = useMemo(
    () => ({
      icon: level?.iconUrl ?? '',
      name: level?.name ?? '',
      minPoints: parseNumber(level?.minPoints),
      minShipments: parseNumber(level?.minShipments),
      minSpend: parseNumber(level?.minSpend),
    }),
    [level]
  );

  const dialogTitle = level ? 'Edit Level' : 'Add Level';

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const formData = new FormData();

      formData.append('name', values.name?.trim() ?? '');
      formData.append('minPoints', String(values.minPoints ?? '0'));
      formData.append('minShipments', String(values.minShipments ?? '0'));
      formData.append('minSpend', String(values.minSpend ?? '0'));

      if (values.icon instanceof File) {
        formData.append('icon', values.icon);
      } else if (typeof values.icon === 'string' && values.icon) {
        formData.append('icon', values.icon);
      }

      const response = level?.id
        ? await dataProvider.update('rewardsLevel', {
            id: level.id,
            data: formData,
          })
        : await dataProvider.post('rewardsLevel', {
            data: formData,
          });

      refresh();
      onClose();

      return response;
    } catch (error: any) {
      const message = error?.message || 'Failed to update rewards level';
      notify(message, 'error');
      return { [FORM_ERROR]: message };
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Form<FormValues>
      key={level?.id ?? 'new-level'}
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <Dialog
            open={open}
            onCancel={onClose}
            title={dialogTitle}
            onConfirm={handleSubmit}
            confirmText="Save"
            submitting={submitting}
            error={submitError}
            classes={{ content: classes.content, root: classes.root }}>
            <div className={classes.layout}>
              <ImageInputField name="icon" />
              <div className={classes.fieldsColumn}>
                <Input
                  name="name"
                  label="Level"
                  validate={required}
                  classes={{ input: classes.inputNoMargin }}
                />
                <div className={classes.numberFields}>
                  <NumberInputFieldWithActions
                    name="minPoints"
                    label="Min points"
                    validate={required}
                    actionButtons
                  />
                  <NumberInputFieldWithActions
                    name="minShipments"
                    label="Min number of shipments"
                    validate={required}
                    actionButtons
                  />
                  <NumberInputFieldWithActions
                    name="minSpend"
                    label="Min Spend"
                    validate={required}
                    actionButtons
                  />
                </div>
              </div>
            </div>
          </Dialog>
        </form>
      )}
    />
  );
};

export default EditLevelDialog;
